import { i18n } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { z } from 'zod';
import { action, can, defineScreen } from '@/shared/screen/define';
import { listPageAction } from '@/shared/patterns/list-search';
import {
  TENANT_REQUEST_PATH,
  TENANT_REQUEST_TABS,
  tenantRequestPath,
} from './tenant-request.search';

const READ = can('TenantRequest', 'Read');
const ref = z.object({ id: z.string().min(1), name: z.string().optional() });
const needsForm = () => {
  throw new Error('This action requires the active partner request screen.');
};
export const tenantRequestCreate = action({
  id: 'tenantRequest.create',
  kind: 'draft',
  label: msg`Adicionar novo parceiro`,
  description: msg`Abre o formulário em branco de solicitação de novo parceiro comercial, com as condições de BaaS, CaaS e Billing.`,
  permission: READ,
  input: z.object({}),
  run: ({ nav }) => nav.to(tenantRequestPath('novo')),
});
export const tenantRequestOpen = action({
  id: 'tenantRequest.open',
  kind: 'navigate',
  label: msg`Abrir solicitação`,
  description: msg`Abre uma solicitação de parceiro pelo id, para conferir ou editar as condições comerciais.`,
  permission: READ,
  input: ref,
  row: true,
  run: ({ nav, input }) => nav.to(tenantRequestPath(input.id)),
});
export const tenantRequestPage = listPageAction('tenantRequest.list.page');
export const tenantRequestRemove = action({
  id: 'tenantRequest.delete',
  kind: 'destructive',
  label: msg`Excluir`,
  description: msg`Abre a exclusão da solicitação de parceiro aberta. Não dá para desfazer. A pessoa confirma com a senha e o código MFA.`,
  permission: READ,
  input: z.strictObject({}),
  // Step-up novo (`TenantRequestWrite`): o assistente só prepara; quem confirma é a pessoa (U24).
  stepUp: true,
  preview: () => ({
    title: i18n._(msg`Excluir parceiro`),
    detail: i18n._(msg`Tem certeza que deseja excluir esse registro?`),
    tone: 'destructive',
  }),
  run: needsForm,
});
export const tenantRequestSave = action({
  id: 'tenantRequest.save',
  kind: 'write',
  label: msg`Salvar`,
  description: msg`Prepara a gravação da solicitação de parceiro aberta, com todas as condições de BaaS, CaaS e Billing. Não muda qual solicitação está aberta nem se ela é nova. Dinheiro vai em centavos inteiros e percentual em fração. A pessoa confirma com a senha e o código MFA.`,
  permission: READ,
  input: z.strictObject({ body: z.record(z.string(), z.unknown()) }),
  // Step-up novo (`TenantRequestWrite`): o assistente só prepara; quem confirma é a pessoa (U24).
  stepUp: true,
  preview: () => ({
    title: i18n._(msg`Salvar solicitação`),
    detail: i18n._(msg`As condições comerciais de BaaS, CaaS e Billing serão gravadas.`),
  }),
  run: needsForm,
});
export const tenantRequestRefresh = action({
  id: 'tenantRequest.refresh',
  kind: 'read',
  label: msg`Atualizar`,
  description: msg`Busca de novo a lista ou a solicitação de parceiro aberta, sem mudar nada.`,
  permission: READ,
  input: z.strictObject({}),
  run: needsForm,
});
export const tenantRequestListScreen = defineScreen({
  id: 'tenantRequest.list',
  path: TENANT_REQUEST_PATH,
  title: msg`Solicitações de novos parceiros`,
  description: msg`Lista de solicitações de novos parceiros comerciais, com paginação, abrir e adicionar novo parceiro.`,
  permission: READ,
  access: { rootTenancy: true },
  place: { icon: 'handshake', section: 'partners' },
  actions: [tenantRequestRefresh, tenantRequestCreate, tenantRequestOpen, tenantRequestPage],
});
export const tenantRequestDetailScreen = defineScreen({
  id: 'tenantRequest.detail',
  path: `${TENANT_REQUEST_PATH}/$id`,
  title: msg`Solicitação de parceiro`,
  description: msg`Cadastro de uma solicitação de parceiro (nova ou edição): parceiro, responsável comercial e condições comerciais nas abas BaaS, CaaS e Billing; salvar, atualizar e excluir.`,
  permission: READ,
  access: { rootTenancy: true },
  place: {
    icon: 'handshake',
    parent: 'tenantRequest.list',
    record: msg`Solicitação`,
    tabs: TENANT_REQUEST_TABS,
  },
  actions: [tenantRequestRemove, tenantRequestRefresh, tenantRequestSave],
});
